import React from "react";
import styled, { css } from "styled-components";
import { Heading } from "./components/styles";

import Tooltip from "./components/Tooltip";
import { Button } from "../../components/Button";
import {
  enhancedVerifications,
  freeVerifications,
  instantVerifications,
} from "./components/constant";

const Container = styled.section`
  max-width: 1280px;
  padding: 52px 92px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: 500px) {
    padding: 10px 24px;
  }
`;

const ButtonWrapper = styled.div`
  margin-block: 20px;
`;

const MainButton = styled(Button)`
  margin-left: 20px;
  margin-top: ${({ marginTop }) => marginTop || "0"};

  a {
    text-decoration: none;
    color: ${({ secondary }) => (secondary ? "#212121" : "white")};
  }
`;

const PricingSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 624px;
  margin-block: 30px;

  .content_item:nth-child(even) {
    background-color: #f6fbff;
  }

  @media screen and (max-width: 500px) {
    width: auto;
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-inline: 1.4rem;
  font-family: "Rubik";
  width: 100%;
`;

const ContentHeader = styled(Content)`
  margin-bottom: 16px;
`;

const Header = styled.p`
  font-weight: 500;
  font-size: 18px;
  line-height: 19px;
  color: #111827;
  text-align: right;
`;

const ContentItem = styled(Content)`
  padding-block: 1.2rem;

  .price {
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    color: #000000;
    margin-left: 35px;
  }
`;

const TooltipWrapper = styled.div`
  display: block;
  :first-child {
    margin-top: 0;
  }

  #tool_tip:before {
    color: "#333333";
    font-size: 18px;
    font-weight: 500;
    ${({ doubleStar, required }) =>
      required
        ? doubleStar
          ? css`
              content: "**";
            `
          : css`
              content: " *";
            `
        : ""}
  }
`;

const Detail = styled.a`
  font-size: 16px;
  color: #154683;
  line-height: 150%;
  font-weight: 400;
  text-decoration: underline;
  margin-right: 3px;

  :first-child {
    margin-top: 0;
  }
`;

const RequiredLabel = styled.p`
  margin-top: 15px;
  font-weight: 200;
  font-size: 16px;
  line-height: 21px;
  color: #666666;
  padding-inline: 0.5rem;
  
  :before {
    color: #333333;
    font-size: 20px;
    font-weight: 500;
    ${({ doubleStar }) =>
      doubleStar
        ? css`
            content: "** ";
          `
        : css`
            content: "* ";
          `}
  }

  a {
    color: #666666;
  }
`;

const FooterLabel = styled.p`
  font-size: 16px;
  line-height: 19px;
  color: #111827;
  margin-block: 20px;
  text-align: center;

  a {
    color: #111827;
  }
`;

const SubHeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-block: 13px;
`;

const SubHeading = styled.div`
  display: flex;
  font-family: Rubik;
  font-size: 18px;
  font-weight: 400;
  color: #212121;
  text-align: center;
  margin-top: 5px;

  p {
    margin: 10px 4px 0px;
  }

  @media screen and (max-width: 500px) {
    font-size: 16px;
    flex-direction: column;
    margin-top: 0;
  }

  @media screen and (max-width: 820px) {
    display: block;
  }
`;

const RequiredWrapper = styled.div`
  margin-top: 30px;
`;

function Pricing() {
  return (
    <Container id="pricing">
      <Heading center secondary>
        Our <span>Pricing</span>
      </Heading>
      <SubHeadingWrapper>
        <SubHeading>
          <p>No set up fee.</p> <p>No minimum monthly commitments.</p>
        </SubHeading>
        <SubHeading>
          <p>No long term contracts.</p> <p>Pay as you go.</p>
        </SubHeading>
      </SubHeadingWrapper>
      <ButtonWrapper>
        <MainButton>
          <a
            href="https://portal.authenticating.com/register"
            target="_blank"
            rel="noreferrer"
          >
            Get started
          </a>
        </MainButton>
        <MainButton secondary>
          <a href="#contact" rel="noreferrer">
            Contact us
          </a>
        </MainButton>
      </ButtonWrapper>
      <PricingSection>
        <ContentHeader>
          <Header>Checks</Header>
          <Header>Price per check</Header>
        </ContentHeader>
        {freeVerifications.map((item) => (
          <ContentItem key={item.id} className="content_item">
            <TooltipWrapper required={item.required}>
              <Detail target="_blank" rel="noreferrer" href={item.link}>
                {item.title}
              </Detail>
              <Tooltip content={item.toolTipNote} id="tool_tip" />
            </TooltipWrapper>
            <p className="price">{item.price}</p>
          </ContentItem>
        ))}
        {instantVerifications.map((item) => (
          <ContentItem key={item.id} className="content_item">
            <TooltipWrapper required={item.required}>
              <Detail target="_blank" rel="noreferrer" href={item.link}>
                {item.title}
              </Detail>
              <Tooltip content={item.toolTipNote} id="tool_tip" />
            </TooltipWrapper>
            <p className="price">{item.price}</p>
          </ContentItem>
        ))}
        {enhancedVerifications.map((item) => (
          <ContentItem key={item.id} className="content_item">
            <TooltipWrapper
              required={item.required}
              doubleStar={item?.doubleStar}
            >
              <Detail target="_blank" rel="noreferrer" href={item.link}>
                {item.title}
              </Detail>
              <Tooltip content={item.toolTipNote} id="tool_tip" />
            </TooltipWrapper>
            <p className="price">{item.price}</p>
          </ContentItem>
        ))}
        <RequiredWrapper>
          <RequiredLabel>
            Only available if the user is verified using Government ID
            Verification, Knowledge Based Authentication, or SSN Verification.
          </RequiredLabel>
          <RequiredLabel doubleStar>
            Varies court to court. Refer to{" "}
            <a
              href="https://docs.authenticating.com/#tag/Methods/paths/~1identity~1user~1search~1counties/post"
              target="_blank"
              rel="noreferrer"
            >
              County History
            </a>{" "}
            for pricing.
          </RequiredLabel>
        </RequiredWrapper>
      </PricingSection>
      <MainButton marginTop="2rem">
        <a
          href="https://portal.authenticating.com/register"
          target="_blank"
          rel="noreferrer"
        >
          Get started
        </a>
      </MainButton>
      <FooterLabel>
        Additional promotional credits are available on top-up. For more info,{" "}
        <a href="#contact" rel="noreferrer">
          contact us
        </a>
        .
      </FooterLabel>
    </Container>
  );
}

export default Pricing;
